import { Alert, Button, Card, Center, PasswordInput, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

interface Props {
  title: string;
  subtitle: string;
  submitLabel: string;
  invalidLinkMessage: string;
  genericErrorMessage: string;
  onSubmit: (token: string, password: string) => Promise<void>;
}

/**
 * Shared by AcceptInvitePage and ResetPasswordPage: a token from the query
 * string, a new password, and a submit that signs the user in on success.
 * "Identical machinery" (ARCHITECTURE.md §6.2 step 5) on the frontend too —
 * the two pages differ only in copy and which auth method they call.
 */
export function SetPasswordCard({
  title,
  subtitle,
  submitLabel,
  invalidLinkMessage,
  genericErrorMessage,
  onSubmit,
}: Props) {
  const { me, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && me) {
    return <Navigate to="/" replace />;
  }

  if (!token) {
    return (
      <Center h="100vh" px="md">
        <Card withBorder shadow="sm" padding="xl" w={420} maw="100%">
          <Alert color="red" title="Invalid link">
            {invalidLinkMessage}
          </Alert>
        </Card>
      </Center>
    );
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(token, password);
      navigate("/", { replace: true });
    } catch (err) {
      // Same generic-error shape as login (§6.2): an invalid, expired, or
      // already-used token and a policy-rejected password both surface here
      // verbatim rather than being reinterpreted.
      setError(err instanceof ApiError ? err.message : genericErrorMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" px="md">
      <Card withBorder shadow="sm" padding="xl" w={420} maw="100%">
        <form onSubmit={handleSubmit}>
          <Stack>
            <div>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            </div>

            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}

            <PasswordInput
              label="Password"
              description="At least 12 characters."
              autoComplete="new-password"
              autoFocus
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            <PasswordInput
              label="Confirm password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
            />

            <Button type="submit" loading={submitting} fullWidth mt="sm">
              {submitLabel}
            </Button>
          </Stack>
        </form>
      </Card>
    </Center>
  );
}
